import { Errors, isDefined, isUndefined, MutableObservableOption, Option, RuntimeNotifier, Terminable } from "@opendaw/lib-std";
import { dbToGain } from "@opendaw/lib-dsp";
import { Promises } from "@opendaw/lib-runtime";
import { Capture } from "./Capture";
import { RecordAudio } from "./RecordAudio";
import { AudioDevices } from "../AudioDevices";
import { RenderQuantum } from "../RenderQuantum";
export class CaptureAudio extends Capture {
    #stream;
    #streamGenerator;
    #isMonitoring = false;
    #requestChannels = Option.None;
    #gainDb = 0.0;
    #audioChain = null;
    #preparedWorklet = null;
    constructor(manager, audioUnitBox, captureAudioBox) {
        super(manager, audioUnitBox, captureAudioBox);
        this.#stream = new MutableObservableOption();
        this.#streamGenerator = Promises.sequentialize(() => this.#updateStream());
        this.ownAll(captureAudioBox.requestChannels.catchupAndSubscribe(owner => {
            const channels = owner.getValue();
            this.#requestChannels = channels === 1 || channels === 2 ? Option.wrap(channels) : Option.None;
            this.#stream.ifSome(stream => this.#rebuildAudioChain(stream));
        }), captureAudioBox.gainDb.catchupAndSubscribe(owner => {
            this.#gainDb = owner.getValue();
            if (isDefined(this.#audioChain)) {
                this.#audioChain.gainNode.gain.value = dbToGain(this.#gainDb);
            }
        }), captureAudioBox.deviceId.catchupAndSubscribe(async () => {
            if (this.armed.getValue()) {
                await this.#streamGenerator();
            }
        }), this.armed.catchupAndSubscribe(async (owner) => {
            const armed = owner.getValue();
            if (armed) {
                await this.#streamGenerator();
            }
            else {
                this.#stopStream();
            }
        }));
    }
    get isMonitoring() { return this.#isMonitoring; }
    set isMonitoring(value) {
        if (this.#isMonitoring === value) {
            return;
        }
        this.#isMonitoring = value;
        if (this.#isMonitoring) {
            this.armed.setValue(true);
            this.#connectMonitoring();
        }
        else {
            this.#disconnectMonitoring();
        }
    }
    get gainDb() { return this.#gainDb; }
    get requestChannels() { return this.#requestChannels; }
    set requestChannels(value) { this.captureBox.requestChannels.setValue(value); }
    get stream() { return this.#stream; }
    get streamDeviceId() {
        return this.streamMediaTrack.map(settings => settings.getSettings().deviceId ?? "");
    }
    get label() { return this.streamMediaTrack.mapOr(track => track.label, "Default"); }
    get deviceLabel() { return this.streamMediaTrack.map(track => track.label ?? ""); }
    get streamMediaTrack() {
        return this.#stream.flatMap(stream => Option.wrap(stream.getAudioTracks().at(0)));
    }
    get outputNode() { return Option.wrap(this.#audioChain?.gainNode); }
    get effectiveChannelCount() { return this.#audioChain?.gainNode.channelCount ?? 1; }
    async prepareRecording() {
        const { project } = this.manager;
        const { env: { audioContext, audioWorklets, sampleManager } } = project;
        if (isUndefined(audioContext.outputLatency)) {
            const approved = RuntimeNotifier.approve({
                headline: "Warning",
                message: "Your browser does not support 'output latency'. This will cause timing issue while recording.",
                approveText: "Ignore",
                cancelText: "Cancel"
            });
            if (!approved) {
                return Promise.reject("Recording cancelled");
            }
        }
        await this.#streamGenerator();
        const audioChain = this.#audioChain;
        if (!isDefined(audioChain)) {
            return Promise.reject("No audio chain available for recording.");
        }
        const { gainNode } = audioChain;
        const channelCount = gainNode.channelCount;
        const recordingWorklet = audioWorklets.createRecording(channelCount, RenderQuantum);
        sampleManager.record(recordingWorklet);
        gainNode.connect(recordingWorklet);
        this.#preparedWorklet = recordingWorklet;
    }
    startRecording() {
        const { project } = this.manager;
        const { env: { audioContext, sampleManager } } = project;
        const audioChain = this.#audioChain;
        const recordingWorklet = this.#preparedWorklet;
        if (!isDefined(audioChain) || !isDefined(recordingWorklet)) {
            console.warn("No audio chain or worklet available for recording.");
            return Terminable.Empty;
        }
        this.#preparedWorklet = null;
        const { gainNode } = audioChain;
        return RecordAudio.start({
            recordingWorklet,
            sourceNode: gainNode,
            sampleManager,
            project,
            capture: this,
            outputLatency: audioContext.outputLatency ?? 0
        });
    }
    async #updateStream() {
        if (this.#stream.nonEmpty()) {
            const stream = this.#stream.unwrap();
            const settings = stream.getAudioTracks().at(0)?.getSettings();
            if (isDefined(settings)) {
                const deviceId = this.deviceId.getValue().unwrapOrUndefined();
                if (deviceId === settings.deviceId) {
                    return Promise.resolve();
                }
            }
        }
        this.#stopStream();
        const deviceId = this.deviceId.getValue().unwrapOrUndefined() ?? AudioDevices.defaultInput?.deviceId;
        const channelCount = this.#requestChannels.unwrapOrElse(1);
        return AudioDevices.requestStream({
            deviceId: isDefined(deviceId) ? { exact: deviceId } : undefined,
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            channelCount: { ideal: channelCount }
        }).then(stream => {
            const tracks = stream.getAudioTracks();
            const track = tracks.at(0);
            const settings = track?.getSettings();
            const gotDeviceId = settings?.deviceId;
            console.debug(`new stream. device requested: ${deviceId ?? "default"}, got: ${gotDeviceId ?? "unknown"}. channelCount requested: ${channelCount}, got: ${settings?.channelCount}`);
            if (isUndefined(deviceId) || deviceId === gotDeviceId) {
                this.#rebuildAudioChain(stream);
                this.#stream.wrap(stream);
            }
            else {
                stream.getAudioTracks().forEach(track => track.stop());
                return Errors.warn(`Could not find audio device with id: '${deviceId}' (got: '${gotDeviceId}')`);
            }
        });
    }
    #stopStream() {
        this.#destroyAudioChain();
        this.#stream.clear(stream => stream.getAudioTracks().forEach(track => track.stop()));
    }
    #rebuildAudioChain(stream) {
        const wasMonitoring = this.#isMonitoring && isDefined(this.#audioChain);
        this.#destroyAudioChain();
        const { audioContext } = this.manager.project.env;
        const sourceNode = audioContext.createMediaStreamSource(stream);
        const gainNode = audioContext.createGain();
        gainNode.gain.value = dbToGain(this.#gainDb);
        const streamChannelCount = stream.getAudioTracks().at(0)?.getSettings().channelCount ?? 1;
        const requestMono = this.#requestChannels.mapOr(channels => channels === 1, false);
        if (requestMono && streamChannelCount === 2) {
            gainNode.channelCount = 1;
            gainNode.channelCountMode = "explicit";
        }
        sourceNode.connect(gainNode);
        this.#audioChain = { sourceNode, gainNode };
        if (wasMonitoring || this.#isMonitoring) {
            this.#connectMonitoring();
        }
    }
    #destroyAudioChain() {
        if (isDefined(this.#audioChain)) {
            const { sourceNode, gainNode } = this.#audioChain;
            sourceNode.disconnect();
            gainNode.disconnect();
            this.#audioChain = null;
        }
    }
    #connectMonitoring() {
        if (isDefined(this.#audioChain)) {
            const { audioContext } = this.manager.project.env;
            this.#audioChain.gainNode.connect(audioContext.destination);
        }
    }
    #disconnectMonitoring() {
        if (isDefined(this.#audioChain)) {
            const { audioContext } = this.manager.project.env;
            this.#audioChain.gainNode.disconnect(audioContext.destination);
        }
    }
}
